import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Phone,
  MessageCircle,
  Clock,
  Send,
  CheckCircle2,
  ShieldCheck,
  Sparkles,
  Mail,
  User,
  Stethoscope,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import useSeo from '../hooks/useSeo';
import {
  getPhonePlaceholder,
  sanitizePhoneInput,
} from '../utils/phone';
import {
  compose,
  email as emailRule,
  errorsFromApi,
  formatError,
  minLength,
  phone as phoneRule,
  required,
  validateForm,
} from '../utils/formValidation';

const SCHEMA = {
  patient_name: compose(required, minLength(2)),
  email: compose(required, emailRule),
  phone: compose(required, phoneRule),
  service_id: required,
  preferred_date: required,
  preferred_time: required,
  notes: compose(required, minLength(5)),
};

const consentValidator = (v) => (v === true ? null : 'validation.consent');

const API_URL =
  import.meta.env.VITE_API_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');

const DEFAULT_CLINIC = {
  clinic_phone: '+90 551 282 42 42',
  clinic_whatsapp: '+90 551 282 42 42',
  clinic_email: 'info@alncrystaldental.com',
  clinic_address: 'Cikcili Mah. Azakoglu Cad. No:35 B Alanya / Antalya',
};

const TIME_SLOTS = [
  '09:00', '09:30', '10:00', '10:30',
  '11:00', '11:30', '12:00', '12:30',
  '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30',
  '17:00', '17:30', '18:00',
];

const sanitizeTel = (v) => String(v || '').replace(/[^\d+]/g, '');
const sanitizeWa = (v) => String(v || '').replace(/[^\d]/g, '');
const todayISO = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

export default function Appointment() {
  const { t, translations, currentLanguage } = useLanguage();
  const [params] = useSearchParams();
  useSeo('appointment', currentLanguage);

  const [clinic, setClinic] = useState(DEFAULT_CLINIC);
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    patient_name: '',
    email: '',
    phone: '',
    service_id: params.get('service') || '',
    preferred_date: '',
    preferred_time: '',
    alternate_date: '',
    notes: '',
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [banner, setBanner] = useState(null);

  const tErr = (key) => formatError(key, t);

  const runField = (name, value) => {
    let err = null;
    if (name === 'consent') err = consentValidator(value);
    else if (SCHEMA[name]) err = SCHEMA[name](value);
    else return;
    setErrors((prev) => {
      const next = { ...prev };
      if (err) next[name] = err;
      else delete next[name];
      return next;
    });
  };

  // Pull clinic settings
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/settings/public?group=contact`);
        if (!res.ok) return;
        const payload = await res.json();
        if (!cancelled && payload?.data) {
          setClinic((prev) => ({ ...prev, ...payload.data }));
        }
      } catch {
        /* keep defaults */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Pull services for the dropdown
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/services?lang=${encodeURIComponent(currentLanguage || 'en')}`
        );
        if (!res.ok) return;
        const payload = await res.json();
        const list = Array.isArray(payload?.data) ? payload.data : [];
        if (!cancelled) {
          // Only top-level services (no parent) -> cleaner dropdown
          const flat = list
            .filter((s) => !s.parent_id)
            .map((s) => ({ id: s.id, title: s.title || s.slug }))
            .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
          setServices(flat);
        }
      } catch {
        if (!cancelled) setServices([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentLanguage]);

  const phoneNumber = useMemo(() => sanitizeTel(clinic?.clinic_phone), [clinic]);
  const waNumber = useMemo(() => sanitizeWa(clinic?.clinic_whatsapp), [clinic]);
  const phonePlaceholder = useMemo(
    () => getPhonePlaceholder(currentLanguage),
    [currentLanguage]
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const next = type === 'checkbox' ? checked : value;
    setForm((prev) => ({ ...prev, [name]: next }));
    if (touched[name]) runField(name, next);
  };

  const handlePhoneChange = (e) => {
    const value = sanitizePhoneInput(e.target.value);
    setForm((prev) => ({ ...prev, phone: value }));
    if (touched.phone) runField('phone', value);
  };

  const handleBlur = (e) => {
    const { name, value, type, checked } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    runField(name, type === 'checkbox' ? checked : value);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBanner(null);

    const found = validateForm(form, SCHEMA);
    const consentErr = consentValidator(form.consent);
    if (consentErr) found.consent = consentErr;

    if (Object.keys(found).length) {
      setErrors(found);
      const nextTouched = Object.keys(found).reduce(
        (acc, k) => ({ ...acc, [k]: true }),
        {}
      );
      setTouched((prev) => ({ ...prev, ...nextTouched }));
      setBanner({
        type: 'error',
        msg: t('validation.fixErrors', 'Please fix the highlighted fields and try again.'),
      });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/appointments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': currentLanguage || 'en',
        },
        body: JSON.stringify({
          patient_name: form.patient_name,
          email: form.email,
          phone: form.phone,
          preferred_date: form.preferred_date || undefined,
          preferred_time: form.preferred_time || undefined,
          alternate_date: form.alternate_date || undefined,
          service_id: form.service_id ? Number(form.service_id) : undefined,
          notes: form.notes || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const apiErrs = errorsFromApi(data);
        if (apiErrs) {
          setErrors(apiErrs);
          setTouched((prev) => ({
            ...prev,
            ...Object.keys(apiErrs).reduce((acc, k) => ({ ...acc, [k]: true }), {}),
          }));
          setBanner({
            type: 'error',
            msg: t('validation.fixErrors', 'Please fix the highlighted fields and try again.'),
          });
        } else if (res.status === 429) {
          setBanner({
            type: 'error',
            msg: t('validation.rateLimit', 'Too many requests — please try again later.'),
          });
        } else {
          setBanner({
            type: 'error',
            msg:
              data?.message ||
              t('validation.serverError', 'Something went wrong on our side. Please try again.'),
          });
        }
        return;
      }

      toast.success(
        t(
          'appointmentPage.form.success',
          "Your appointment request has been received! We'll call you within a few hours to confirm."
        )
      );
      setDone(true);
    } catch (err) {
      setBanner({
        type: 'error',
        msg: t('validation.serverError', 'Something went wrong on our side. Please try again.'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const sideItems = Array.isArray(translations?.appointmentPage?.side?.items)
    ? translations.appointmentPage.side.items
    : [];

  return (
    <div className="appointment-page">
      {/* Hero */}
      <section className="appointment-hero">
        <div className="container-premium">
          <div className="appointment-hero-inner">
            <span className="section-eyebrow light">
              {t('appointmentPage.hero.eyebrow', 'Book an appointment')}
            </span>
            <h1 className="appointment-hero-title">
              {t(
                'appointmentPage.hero.title',
                'Schedule your visit at Crystal Dental'
              )}
            </h1>
            <p className="appointment-hero-sub">
              {t(
                'appointmentPage.hero.subtitle',
                "Pick a treatment, share your preferred date and we'll confirm your slot within a few hours. Free online consultation included."
              )}
            </p>
            <div className="appointment-hero-badges">
              <span className="appointment-hero-badge">
                <Sparkles size={14} />
                {t('appointmentPage.hero.badges.free', 'Free consultation')}
              </span>
              <span className="appointment-hero-badge">
                <CheckCircle2 size={14} />
                {t('appointmentPage.hero.badges.fast', 'Reply in ~5 min')}
              </span>
              <span className="appointment-hero-badge">
                <ShieldCheck size={14} />
                {t('appointmentPage.hero.badges.secure', 'Your data is secure')}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Form + side */}
      <section className="section-padding section-tone-a">
        <div className="container-premium">
          <div className="appointment-grid">
            {/* Form */}
            <div className="appointment-form-card">
              <header className="appointment-form-head">
                <h2 className="appointment-form-title">
                  {t('appointmentPage.form.title', 'Appointment details')}
                </h2>
                <p className="appointment-form-sub">
                  {t(
                    'appointmentPage.form.subtitle',
                    "All fields with * are required. We'll contact you to confirm the exact time."
                  )}
                </p>
              </header>

              {done ? (
                <div className="appointment-success">
                  <div className="appointment-success-icon">
                    <CheckCircle2 size={32} />
                  </div>
                  <h3>
                    {t(
                      'appointmentPage.form.success',
                      "Your appointment request has been received! We'll call you within a few hours to confirm."
                    )}
                  </h3>
                  <div className="appointment-success-cta">
                    {waNumber ? (
                      <a
                        href={`https://wa.me/${waNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-crystal"
                      >
                        <MessageCircle size={16} />
                        {t('appointmentPage.side.whatsapp', 'WhatsApp us')}
                      </a>
                    ) : null}
                    <Link to="/" className="btn-crystal-outline">
                      {t('common.back_home', 'Back to home')}
                      <ChevronRight size={16} />
                    </Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="appointment-form" noValidate>
                  {banner && (
                    <div className={`form-banner ${banner.type}`} role="alert">
                      <AlertCircle size={18} />
                      <span>{banner.msg}</span>
                    </div>
                  )}

                  {/* Step 1: contact */}
                  <div className="appointment-step">
                    <div className="appointment-step-head">
                      <span className="appointment-step-num">1</span>
                      <h3>
                        <User size={16} />
                        {t('appointmentPage.form.step1', 'Your details')}
                      </h3>
                    </div>
                    <div className="appointment-fields two">
                      <div className={`field ${errors.patient_name ? 'has-error' : ''}`}>
                        <label htmlFor="ap-patient_name" className="field-label">
                          {t('appointmentPage.form.fullName', 'Full name')} <span className="req">*</span>
                        </label>
                        <input
                          id="ap-patient_name"
                          name="patient_name"
                          className="field-input"
                          value={form.patient_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          aria-invalid={Boolean(errors.patient_name)}
                          aria-describedby={errors.patient_name ? 'ap-patient_name-err' : undefined}
                          placeholder={t(
                            'appointmentPage.form.fullNamePlaceholder',
                            'First and last name'
                          )}
                        />
                        {errors.patient_name && (
                          <span id="ap-patient_name-err" className="field-error">
                            <AlertCircle size={13} /> {tErr(errors.patient_name)}
                          </span>
                        )}
                      </div>
                      <div className={`field ${errors.phone ? 'has-error' : ''}`}>
                        <label htmlFor="ap-phone" className="field-label">
                          {t('appointmentPage.form.phone', 'Phone / WhatsApp')} <span className="req">*</span>
                        </label>
                        <input
                          id="ap-phone"
                          name="phone"
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          className="field-input"
                          value={form.phone}
                          onChange={handlePhoneChange}
                          onBlur={handleBlur}
                          aria-invalid={Boolean(errors.phone)}
                          aria-describedby={errors.phone ? 'ap-phone-err' : undefined}
                          placeholder={phonePlaceholder}
                        />
                        {errors.phone && (
                          <span id="ap-phone-err" className="field-error">
                            <AlertCircle size={13} /> {tErr(errors.phone)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={`field ${errors.email ? 'has-error' : ''}`}>
                      <label htmlFor="ap-email" className="field-label">
                        {t('appointmentPage.form.email', 'Email address')} <span className="req">*</span>
                      </label>
                      <input
                        id="ap-email"
                        name="email"
                        type="email"
                        className="field-input"
                        value={form.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? 'ap-email-err' : undefined}
                        placeholder={t(
                          'appointmentPage.form.emailPlaceholder',
                          'you@example.com'
                        )}
                      />
                      {errors.email && (
                        <span id="ap-email-err" className="field-error">
                          <AlertCircle size={13} /> {tErr(errors.email)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Step 2: treatment + date/time */}
                  <div className="appointment-step">
                    <div className="appointment-step-head">
                      <span className="appointment-step-num">2</span>
                      <h3>
                        <Stethoscope size={16} />
                        {t('appointmentPage.form.step2', 'Treatment & schedule')}
                      </h3>
                    </div>
                    <div className={`field ${errors.service_id ? 'has-error' : ''}`}>
                      <label htmlFor="ap-service_id" className="field-label">
                        {t('appointmentPage.form.service', 'Treatment of interest')}{' '}
                        <span className="req">*</span>
                      </label>
                      <select
                        id="ap-service_id"
                        name="service_id"
                        className="field-input"
                        value={form.service_id}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        aria-invalid={Boolean(errors.service_id)}
                        aria-describedby={errors.service_id ? 'ap-service_id-err' : undefined}
                      >
                        <option value="">
                          {t(
                            'appointmentPage.form.serviceAny',
                            'Not sure yet — recommend something'
                          )}
                        </option>
                        {services.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.title}
                          </option>
                        ))}
                      </select>
                      {errors.service_id && (
                        <span id="ap-service_id-err" className="field-error">
                          <AlertCircle size={13} /> {tErr(errors.service_id)}
                        </span>
                      )}
                    </div>

                    <div className="appointment-fields two">
                      <div className={`field ${errors.preferred_date ? 'has-error' : ''}`}>
                        <label htmlFor="ap-preferred_date" className="field-label">
                          {t('appointmentPage.form.preferredDate', 'Preferred date')}{' '}
                          <span className="req">*</span>
                        </label>
                        <input
                          id="ap-preferred_date"
                          name="preferred_date"
                          type="date"
                          className="field-input"
                          value={form.preferred_date}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          min={todayISO()}
                          aria-invalid={Boolean(errors.preferred_date)}
                          aria-describedby={errors.preferred_date ? 'ap-preferred_date-err' : undefined}
                        />
                        {errors.preferred_date && (
                          <span id="ap-preferred_date-err" className="field-error">
                            <AlertCircle size={13} /> {tErr(errors.preferred_date)}
                          </span>
                        )}
                      </div>
                      <div className={`field ${errors.preferred_time ? 'has-error' : ''}`}>
                        <label htmlFor="ap-preferred_time" className="field-label">
                          {t('appointmentPage.form.preferredTime', 'Preferred time')}{' '}
                          <span className="req">*</span>
                        </label>
                        <select
                          id="ap-preferred_time"
                          name="preferred_time"
                          className="field-input"
                          value={form.preferred_time}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          aria-invalid={Boolean(errors.preferred_time)}
                          aria-describedby={errors.preferred_time ? 'ap-preferred_time-err' : undefined}
                        >
                          <option value="">
                            {t('appointmentPage.form.anyTime', 'Select a time')}
                          </option>
                          {TIME_SLOTS.map((slot) => (
                            <option key={slot} value={slot}>
                              {slot}
                            </option>
                          ))}
                        </select>
                        {errors.preferred_time && (
                          <span id="ap-preferred_time-err" className="field-error">
                            <AlertCircle size={13} /> {tErr(errors.preferred_time)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="field">
                      <label htmlFor="ap-alternate_date" className="field-label">
                        {t(
                          'appointmentPage.form.alternateDate',
                          'Alternative date (optional)'
                        )}
                      </label>
                      <input
                        id="ap-alternate_date"
                        name="alternate_date"
                        type="date"
                        className="field-input"
                        value={form.alternate_date}
                        onChange={handleChange}
                        min={todayISO()}
                      />
                    </div>
                  </div>

                  {/* Step 3: notes */}
                  <div className="appointment-step">
                    <div className="appointment-step-head">
                      <span className="appointment-step-num">3</span>
                      <h3>
                        <Mail size={16} />
                        {t('appointmentPage.form.step3', 'Notes')}
                      </h3>
                    </div>
                    <div className={`field ${errors.notes ? 'has-error' : ''}`}>
                      <label htmlFor="ap-notes" className="field-label">
                        {t('appointmentPage.form.notes', 'Notes for the doctor')}{' '}
                        <span className="req">*</span>
                      </label>
                      <textarea
                        id="ap-notes"
                        name="notes"
                        rows={4}
                        className="field-textarea"
                        value={form.notes}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        aria-invalid={Boolean(errors.notes)}
                        aria-describedby={errors.notes ? 'ap-notes-err' : undefined}
                        placeholder={t(
                          'appointmentPage.form.notesPlaceholder',
                          'Tell us about your goals, current symptoms, prior treatments, etc.'
                        )}
                      />
                      {errors.notes && (
                        <span id="ap-notes-err" className="field-error">
                          <AlertCircle size={13} /> {tErr(errors.notes)}
                        </span>
                      )}
                    </div>
                    <div className={`field ${errors.consent ? 'has-error' : ''}`}>
                      <label className="appointment-consent">
                        <input
                          type="checkbox"
                          name="consent"
                          checked={form.consent}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          aria-invalid={Boolean(errors.consent)}
                          aria-describedby={errors.consent ? 'ap-consent-err' : undefined}
                        />
                        <span>
                          {t(
                            'appointmentPage.form.consent',
                            'I agree to be contacted about my appointment via phone, WhatsApp or email.'
                          )}
                        </span>
                      </label>
                      {errors.consent && (
                        <span id="ap-consent-err" className="field-error">
                          <AlertCircle size={13} /> {tErr(errors.consent)}
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn-crystal full appointment-submit"
                    disabled={submitting}
                  >
                    {submitting ? (
                      t('appointmentPage.form.submitting', 'Sending...')
                    ) : (
                      <>
                        <Send size={16} />
                        {t('appointmentPage.form.submit', 'Request Appointment')}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Side panel */}
            <aside className="appointment-side">
              <div className="appointment-side-card appointment-side-primary">
                <h3 className="appointment-side-title">
                  <Sparkles size={16} />
                  {t('appointmentPage.side.title', 'Why book with us')}
                </h3>
                <ul className="appointment-side-list">
                  {sideItems.map((it, idx) => (
                    <li key={idx}>
                      <CheckCircle2 size={16} />
                      <div>
                        <strong>{it.label}</strong>
                        <span>{it.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="appointment-side-card">
                <h3 className="appointment-side-title">
                  <Phone size={16} />
                  {t('appointmentPage.side.callTitle', 'Prefer to talk?')}
                </h3>
                <p className="appointment-side-desc">
                  {t(
                    'appointmentPage.side.callDesc',
                    'Our patient coordinator is one click away.'
                  )}
                </p>
                <div className="appointment-side-actions">
                  {waNumber ? (
                    <a
                      href={`https://wa.me/${waNumber}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-crystal full"
                    >
                      <MessageCircle size={16} />
                      {t('appointmentPage.side.whatsapp', 'WhatsApp us')}
                    </a>
                  ) : null}
                  {phoneNumber ? (
                    <a
                      href={`tel:${phoneNumber}`}
                      className="btn-crystal-outline full"
                    >
                      <Phone size={16} />
                      {t('appointmentPage.side.call', 'Call us')}
                    </a>
                  ) : null}
                </div>
              </div>

              <div className="appointment-side-card">
                <h3 className="appointment-side-title">
                  <Clock size={16} />
                  {t('appointmentPage.side.hoursTitle', 'Opening hours')}
                </h3>
                <ul className="appointment-hours">
                  <li>
                    <span>{t('appointmentPage.side.weekdays', 'Mon – Fri')}</span>
                    <strong>
                      {t('appointmentPage.side.weekdaysHours', '09:00 – 19:00')}
                    </strong>
                  </li>
                  <li>
                    <span>{t('appointmentPage.side.saturday', 'Saturday')}</span>
                    <strong>
                      {t('appointmentPage.side.saturdayHours', '09:00 – 17:00')}
                    </strong>
                  </li>
                  <li className="is-closed">
                    <span>{t('appointmentPage.side.sunday', 'Sunday')}</span>
                    <strong>
                      {t('appointmentPage.side.closed', 'Closed')}
                    </strong>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
